import { isAutoCrossLevel, type AutoCrossLevel } from '@nonogram/shared';
import type { ColorThemePreference } from '../../utils/color-theme.js';
import type { GameStore } from '../../state/game-store.js';
import type { ClueMarkingMode } from '../../engine/types.js';
import type { CellStyle, HeaderSizing } from '../../renderer/board-renderer.js';
import { DEFAULT_CELL_STYLE, isCellStyle } from '../../renderer/pixi/cell-style.js';
import { DEFAULT_COLUMN_HEADER_MAX, DEFAULT_ROW_HEADER_MAX, MIN_CLUE_SQUISH } from '../../renderer/board-frame.js';
import { clampLineCellPx } from '../components/game/line-view-model.js';
import { browserStorage } from '../../utils/browser-storage.js';

export interface GameOptions {
  touchMode: 'paint' | 'pan' | 'cursor';
  cursorSensitivity: number;
  autoCrossLevel: AutoCrossLevel;
  /** Cross off clues automatically as they are solved, or by tapping them. */
  clueMarking: ClueMarkingMode;
  showLineHints: boolean;
  /** A moment after the board changes, look for mistakes and offer to fix them (and in solo play to back up). */
  autoCheck: boolean;
  colorTheme: ColorThemePreference;
  darkenPuzzleColors: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
  /** How cells and clues are drawn: bevelled blocks or regions with tiled clues, or flat. */
  cellStyle: CellStyle;
  soundVolume: number;
  musicVolume: number;
  hapticFeedback: boolean;
  dragRunBadgeSide: 'left' | 'right';
  /** Measure lines from the cursor to the next different cell: always, only in the line view, only while Shift is held, or never. */
  measureLines: MeasureLinesOption;
  /** Row clue slot width as a fraction of the cell size. */
  clueSquishRow: number;
  /** Column clue slot height as a fraction of the cell size. */
  clueSquishColumn: number;
  /** Row header width cap as a fraction of the screen (1 = no cap). Set by the resize grip too. */
  rowHeaderMax: number;
  /** Column header height cap as a fraction of the screen (1 = no cap). Set by the resize grip too. */
  columnHeaderMax: number;
  /** Fold solved edge rows/columns away, with the clues they cover. */
  hideSolvedEdges: boolean;
  /** Hide crossed-off clues at either end of each line's clues (shown again while the cursor is on their cells). */
  hideSolvedClues: boolean;
  /** Line view cell size in px (its zoom), or null to fit the width. */
  lineCellPx: number | null;
}

export type MeasureLinesOption = 'off' | 'on' | 'lineView' | 'shift';

export const MIN_HEADER_MAX = 0.04;

export const defaultOptions: GameOptions = {
  touchMode: 'paint',
  cursorSensitivity: 0.5,
  autoCrossLevel: 'off',
  clueMarking: 'auto',
  showLineHints: false,
  autoCheck: true,
  colorTheme: 'light',
  darkenPuzzleColors: false,
  highContrast: false,
  reducedMotion: false,
  cellStyle: DEFAULT_CELL_STYLE,
  soundVolume: 0.4,
  musicVolume: 0.3,
  hapticFeedback: true,
  dragRunBadgeSide: 'left',
  measureLines: 'lineView',
  clueSquishRow: 1,
  clueSquishColumn: 1,
  rowHeaderMax: DEFAULT_ROW_HEADER_MAX,
  columnHeaderMax: DEFAULT_COLUMN_HEADER_MAX,
  hideSolvedEdges: false,
  hideSolvedClues: false,
  lineCellPx: null,
};

export const OPTIONS_STORAGE_KEY = 'nono_game_options';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function sanitizeBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function sanitizeNumber(value: unknown, fallback: number, min: number, max: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? clamp(value, min, max)
    : fallback;
}

function sanitizeTouchMode(value: unknown): GameOptions['touchMode'] | null {
  return value === 'paint' || value === 'pan' || value === 'cursor' ? value : null;
}

function sanitizeAutoCrossLevel(value: unknown): AutoCrossLevel | null {
  return isAutoCrossLevel(value) ? value : null;
}

/** How an auto-cross level reads in settings (solo and the room's). */
export const AUTO_CROSS_LABELS: Record<AutoCrossLevel, string> = {
  off: 'Off',
  line_complete: 'Finished lines',
  line_and_separators: 'Finished lines and gaps',
};

function sanitizeClueMarking(value: unknown): GameOptions['clueMarking'] | null {
  return value === 'auto' || value === 'manual' ? value : null;
}

function sanitizeDragRunBadgeSide(value: unknown): GameOptions['dragRunBadgeSide'] | null {
  return value === 'left' || value === 'right' ? value : null;
}

function sanitizeMeasureLines(value: unknown): MeasureLinesOption | null {
  return value === 'off' || value === 'on' || value === 'lineView' || value === 'shift' ? value : null;
}

/** Where measure lines show for the option: on the board and in the line view, with Shift held or not. */
export function measureLinesShown(option: MeasureLinesOption, shiftHeld: boolean): { board: boolean; lineView: boolean } {
  switch (option) {
    case 'on': return { board: true, lineView: true };
    case 'lineView': return { board: false, lineView: true };
    case 'shift': return { board: shiftHeld, lineView: shiftHeld };
    default: return { board: false, lineView: false };
  }
}

function sanitizeColorTheme(value: unknown): GameOptions['colorTheme'] | null {
  return value === 'light' || value === 'dark' || value === 'system' ? value : null;
}

function sanitizeOptions(value: unknown): GameOptions {
  const record = isRecord(value) ? value : {};

  return {
    touchMode: sanitizeTouchMode(record.touchMode) ?? defaultOptions.touchMode,
    cursorSensitivity: sanitizeNumber(
      record.cursorSensitivity,
      defaultOptions.cursorSensitivity,
      0,
      1,
    ),
    autoCrossLevel: sanitizeAutoCrossLevel(record.autoCrossLevel) ?? defaultOptions.autoCrossLevel,
    clueMarking: sanitizeClueMarking(record.clueMarking) ?? defaultOptions.clueMarking,
    showLineHints: sanitizeBoolean(record.showLineHints, defaultOptions.showLineHints),
    autoCheck: sanitizeBoolean(record.autoCheck, defaultOptions.autoCheck),
    colorTheme: sanitizeColorTheme(record.colorTheme) ?? defaultOptions.colorTheme,
    darkenPuzzleColors: sanitizeBoolean(
      record.darkenPuzzleColors,
      defaultOptions.darkenPuzzleColors,
    ),
    highContrast: sanitizeBoolean(record.highContrast, defaultOptions.highContrast),
    // Until the player picks, the setting starts from the system preference.
    reducedMotion: typeof record.reducedMotion === 'boolean' ? record.reducedMotion : systemPrefersReducedMotion(),
    cellStyle: isCellStyle(record.cellStyle) ? record.cellStyle : defaultOptions.cellStyle,
    soundVolume: sanitizeNumber(record.soundVolume, defaultOptions.soundVolume, 0, 1),
    musicVolume: sanitizeNumber(record.musicVolume, defaultOptions.musicVolume, 0, 1),
    hapticFeedback: sanitizeBoolean(record.hapticFeedback, defaultOptions.hapticFeedback),
    dragRunBadgeSide: sanitizeDragRunBadgeSide(record.dragRunBadgeSide) ?? defaultOptions.dragRunBadgeSide,
    measureLines: sanitizeMeasureLines(record.measureLines) ?? defaultOptions.measureLines,
    clueSquishRow: sanitizeNumber(record.clueSquishRow, defaultOptions.clueSquishRow, MIN_CLUE_SQUISH, 1),
    clueSquishColumn: sanitizeNumber(record.clueSquishColumn, defaultOptions.clueSquishColumn, MIN_CLUE_SQUISH, 1),
    rowHeaderMax: sanitizeNumber(record.rowHeaderMax, defaultOptions.rowHeaderMax, MIN_HEADER_MAX, 1),
    columnHeaderMax: sanitizeNumber(record.columnHeaderMax, defaultOptions.columnHeaderMax, MIN_HEADER_MAX, 1),
    hideSolvedEdges: sanitizeBoolean(record.hideSolvedEdges, defaultOptions.hideSolvedEdges),
    hideSolvedClues: sanitizeBoolean(record.hideSolvedClues, defaultOptions.hideSolvedClues),
    lineCellPx: typeof record.lineCellPx === 'number' && Number.isFinite(record.lineCellPx)
      ? clampLineCellPx(record.lineCellPx)
      : null,
  };
}

export class OptionsService {
  constructor(
    private readonly storage: StorageLike | null = browserStorage(),
    private readonly storageKey: string = OPTIONS_STORAGE_KEY,
  ) {}

  load(): GameOptions {
    let stored: unknown = {};
    try {
      const raw = this.storage?.getItem(this.storageKey);
      if (raw) stored = JSON.parse(raw);
    } catch {
      // Unreadable or corrupt options fall back to the defaults.
    }
    return sanitizeOptions(stored);
  }

  getOptions(): GameOptions {
    return this.load();
  }

  save(options: Partial<GameOptions>): GameOptions {
    const next = sanitizeOptions({ ...this.load(), ...options });

    if (!this.storage) return next;

    try {
      this.storage.setItem(this.storageKey, JSON.stringify(next));
    } catch {
      // Ignore write failures and keep the in-memory return value usable.
    }

    return next;
  }

  setOption<K extends keyof GameOptions>(key: K, value: GameOptions[K]): GameOptions {
    return this.save({ [key]: value } as Pick<GameOptions, K>);
  }
}

/** The renderer's header sizing from options. */
export function headerSizingFromOptions(options: GameOptions): HeaderSizing {
  return {
    rowSquish: options.clueSquishRow,
    columnSquish: options.clueSquishColumn,
    rowHeaderMax: options.rowHeaderMax,
    columnHeaderMax: options.columnHeaderMax,
  };
}

export function syncOptionsToStore(
  store: Pick<GameStore, 'dispatch' | 'getState'>,
  options: GameOptions,
): void {
  const state = store.getState();

  if (state.assists.showLineHints !== options.showLineHints) {
    store.dispatch({
      type: 'LINE_HINT_ASSISTANCE_CHANGED',
      enabled: options.showLineHints,
    });
  }

  if (state.assists.autoCheck !== options.autoCheck) {
    store.dispatch({ type: 'AUTO_CHECK_ASSIST_CHANGED', enabled: options.autoCheck });
  }

  if (state.assists.autoCrossAssist !== options.autoCrossLevel) {
    store.dispatch({
      type: 'AUTO_CROSS_ASSIST_CHANGED',
      level: options.autoCrossLevel,
    });
  }

  if (state.assists.clueMarking !== options.clueMarking) {
    store.dispatch({
      type: 'CLUE_MARKING_CHANGED',
      mode: options.clueMarking,
    });
  }
}

/** The system's reduced-motion preference: the setting's default until the player saves one. */
export function systemPrefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}
